import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, RefreshCw, Lock, ShieldCheck } from 'lucide-react';
import { clearSignoutReason, peekSignoutReason, signoutMessage, startIdleSession } from '../utils/idleSession';

type Step = 'credentials' | 'new_password' | 'totp_code';

export function LoginPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [step,     setStep]     = useState<Step>('credentials');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Why the user was sent here (inactivity / expired session), if they were.
  const [notice] = useState<string | null>(() => signoutMessage(peekSignoutReason()));
  useEffect(() => { clearSignoutReason(); }, []);

  const finishLogin = async () => {
    startIdleSession();   // start the 15-minute inactivity clock
    await refreshUser();
    navigate('/dashboard');
  };

  const friendlyError = (e: any): string => {
    const name = e?.name ?? '';
    const msg  = e?.message ?? '';
    if (name === 'UserDisabledException' || msg.includes('disabled')) {
      return 'Your account has been disabled. Contact your administrator for help.';
    }
    if (name === 'NotAuthorizedException' && msg.toLowerCase().includes('attempts exceeded')) {
      return 'Too many failed attempts. Please wait a few minutes and try again.';
    }
    if (name === 'NotAuthorizedException') {
      return 'Incorrect email or password.';
    }
    if (name === 'UserNotFoundException') {
      return 'Incorrect email or password.';
    }
    if (name === 'CodeMismatchException') {
      return 'That code is incorrect. Please try again.';
    }
    return msg || 'Something went wrong. Please try again.';
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        await finishLogin();
        return;
      }

      const nextStep = result.nextStep?.signInStep;
      if (nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('new_password');
      } else if (nextStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp_code');
      } else {
        setError(`Unexpected sign-in step: ${nextStep}. Contact support if this persists.`);
      }
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        await finishLogin();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp_code');
      } else {
        setError('Unexpected next step. Contact support if this persists.');
      }
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: totpCode });
      if (result.isSignedIn) {
        await finishLogin();
      } else {
        setError('Unexpected next step. Contact support if this persists.');
      }
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">256 Metrix</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">

          {notice && step === 'credentials' && (
            <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" role="status">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">{notice}</p>
            </div>
          )}

          {step === 'credentials' && (
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  required autoFocus
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit" disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                <span>{loading ? 'Signing in…' : 'Sign In'}</span>
              </button>

              <div className="text-center">
                <a href="/forgot-password" className="text-xs text-blue-600 hover:underline">Forgot your password?</a>
              </div>
            </form>
          )}

          {step === 'new_password' && (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">You need to set a new password before continuing.</p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                <input
                  type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required autoFocus
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
                <input
                  type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit" disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                <span>{loading ? 'Saving…' : 'Set Password & Continue'}</span>
              </button>
            </form>
          )}

          {step === 'totp_code' && (
            <form onSubmit={handleTotpSubmit} className="space-y-4">
              <div className="flex items-center space-x-2 text-gray-700">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <p className="text-sm">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <div>
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  required autoFocus placeholder="123456"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit" disabled={loading || totpCode.length !== 6}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                <span>{loading ? 'Verifying…' : 'Verify & Sign In'}</span>
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
